import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Tabs,
  Tab,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  Receipt as ReceiptIcon,
  LocalAtm as LocalAtmIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import FinanceContainer from "../containers/FinanceContainer";
import { useRouter } from "../../../hooks/useRouter";
import config from "../../../config";
import { formatCurrency } from "../../../utils";

interface Debtor {
  id: string;
  fullName: string;
  email?: string;
  secondaryEmail?: string;
  tertiaryEmail?: string;
  contactPhone?: string;
  unitRef?: string;
  developmentName?: string;
  propertyType?: string;
  monthlyInstalment?: number;
  balance?: number;
  depositPaid?: number;
  purchasePrice?: number;
  salesRep?: string;
  notes?: string;
  active?: boolean;
}

interface DebtorInvoice {
  id: string;
  invoiceNo: string;
  invoiceDate: string;
  dueDate?: string;
  description?: string;
  amountIncl: number;
  amountPaid?: number;
  status: string;
}

interface Receipt {
  id: string;
  receiptNo: string;
  receiptDate: string;
  amount: number;
  cashbook?: string;
  reference?: string;
  notes?: string;
  receiptType?: string;
  paymentType?: string;
}

interface DebtorFullData {
  id: string;
  invoices: DebtorInvoice[];
  receipts: Receipt[];
  summary: {
    totalInvoiced: number;
    totalReceipts: number;
    balance: number;
    paymentBreakdown?: {
      installmentInvoiced: number;
      interestInvoiced: number;
      vatInvoiced: number;
      installmentPayments: number;
      interestPayments: number;
      vatPayments: number;
      otherPayments: number;
      installmentBalance: number;
      interestBalance: number;
      vatBalance: number;
    };
  };
  fullName: string;
  email?: string;
  secondaryEmail?: string;
  tertiaryEmail?: string;
  contactPhone?: string;
  unitRef?: string;
  developmentName?: string;
  propertyType?: string;
  monthlyInstalment?: number;
  balance?: number;
  depositPaid?: number;
  purchasePrice?: number;
  salesRep?: string;
  notes?: string;
  active?: boolean;
}

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString("en-GB") : "-";

const getInvoiceRowKey = (invoice: DebtorInvoice, index: number) =>
  invoice.id ||
  `${invoice.invoiceNo || "invoice"}-${invoice.invoiceDate || index}-${index}`;

const getReceiptRowKey = (receipt: Receipt, index: number) =>
  receipt.id ||
  `${receipt.receiptNo || "receipt"}-${receipt.receiptDate || index}-${index}`;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const DebtorDetail: React.FC = () => {
  const router = useRouter();
  const { id } = router.params as { id?: string };
  const [debtor, setDebtor] = useState<DebtorFullData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [receiptTabValue, setReceiptTabValue] = useState(0);

  const fetchDebtor = useCallback(async () => {
    if (!id) {
      setError("Missing debtor ID");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("vms_access_token");
      const response = await fetch(
        `${config.API_BASE_URL}/debtors/${id}/full`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to load debtor profile");
      }

      const data = await response.json();
      setDebtor(data);
    } catch (err: any) {
      setError(err.message || "Failed to load debtor profile");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDebtor();
  }, [fetchDebtor]);

  const summaryCards = useMemo(
    () =>
      debtor
        ? [
            {
              label: "Current Balance",
              value: formatCurrency(debtor.summary.balance),
            },
            {
              label: "Installment Balance",
              value: formatCurrency(
                debtor.summary.paymentBreakdown?.installmentBalance || 0,
              ),
            },
            {
              label: "Interest Balance",
              value: formatCurrency(
                debtor.summary.paymentBreakdown?.interestBalance || 0,
              ),
            },
            {
              label: "VAT Balance",
              value: formatCurrency(
                debtor.summary.paymentBreakdown?.vatBalance || 0,
              ),
            },
          ]
        : [],
    [debtor],
  );

  const installmentReceipts = useMemo(
    () =>
      debtor?.receipts.filter(
        (r) => r.paymentType === "installment" || r.receiptType === "payment",
      ) || [],
    [debtor?.receipts],
  );

  const vatReceipts = useMemo(
    () =>
      debtor?.receipts.filter(
        (r) => r.paymentType === "vat" || r.receiptType === "vat",
      ) || [],
    [debtor?.receipts],
  );

  const breakdownCards = useMemo(
    () =>
      debtor?.summary.paymentBreakdown
        ? [
            {
              label: "Installment Paid",
              value: formatCurrency(
                debtor.summary.paymentBreakdown.installmentPayments,
              ),
              backgroundColor: "#e3f2fd",
              icon: <LocalAtmIcon color="primary" />,
            },
            {
              label: "Interest Paid",
              value: formatCurrency(
                debtor.summary.paymentBreakdown.interestPayments,
              ),
              backgroundColor: "#ede7f6",
              icon: <AssignmentIcon sx={{ color: "#673ab7" }} />,
            },
            {
              label: "VAT Paid",
              value: formatCurrency(
                debtor.summary.paymentBreakdown.vatPayments,
              ),
              backgroundColor: "#f3e5f5",
              icon: <AssignmentIcon sx={{ color: "#9c27b0" }} />,
            },
            {
              label: "Total Receipts",
              value: formatCurrency(debtor.summary.totalReceipts),
              backgroundColor: "#e8f5e9",
              icon: <ReceiptIcon sx={{ color: "#2e7d32" }} />,
            },
          ]
        : [],
    [debtor],
  );

  return (
    <FinanceContainer>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <IconButton onClick={() => router.push("/finance/debtors")}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Debtor Profile
            </Typography>
            <Typography color="text.secondary">
              Customer finance record, invoices, and receipts.
            </Typography>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : debtor ? (
          <>
            <Card sx={{ mb: 3, borderRadius: 3 }}>
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Typography variant="h5" fontWeight={700}>
                      {debtor.fullName}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        mt: 2,
                        mb: 2,
                      }}
                    >
                      <Chip
                        icon={<HomeIcon />}
                        label={debtor.unitRef || "No unit"}
                        variant="outlined"
                      />
                      <Chip
                        icon={<ReceiptIcon />}
                        label={debtor.propertyType || "Property type not set"}
                        variant="outlined"
                      />
                      <Chip
                        label={debtor.active ? "Active" : "Inactive"}
                        color={debtor.active ? "success" : "default"}
                      />
                    </Box>
                    <Typography color="text.secondary">
                      {debtor.developmentName || "No development assigned"}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                      {debtor.contactPhone || "No phone number"}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                      {debtor.email || "No email address"}
                    </Typography>
                    {debtor.secondaryEmail && (
                      <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                        {debtor.secondaryEmail}
                      </Typography>
                    )}
                    {debtor.tertiaryEmail && (
                      <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                        {debtor.tertiaryEmail}
                      </Typography>
                    )}
                    {debtor.salesRep ? (
                      <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                        Sales rep: {debtor.salesRep}
                      </Typography>
                    ) : null}
                    {debtor.notes ? (
                      <Typography sx={{ mt: 2 }}>{debtor.notes}</Typography>
                    ) : null}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: "#f8fbff",
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary">
                        Purchase Price
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {formatCurrency(debtor.purchasePrice || 0)}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mt: 2 }}
                      >
                        Deposit Paid
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {formatCurrency(debtor.depositPaid || 0)}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              {summaryCards.map((item) => (
                <Grid item xs={12} sm={6} md={3} key={item.label}>
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        {item.label}
                      </Typography>
                      <Typography variant="h5" fontWeight={700} sx={{ mt: 1 }}>
                        {item.value}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {debtor.summary.paymentBreakdown && (
              <Grid container spacing={3} sx={{ mb: 3 }}>
                {breakdownCards.map((item) => (
                  <Grid item xs={12} sm={6} md={3} key={item.label}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        backgroundColor: item.backgroundColor,
                      }}
                    >
                      <CardContent>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {item.icon}
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            {item.label}
                          </Typography>
                        </Box>
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          sx={{ mt: 1 }}
                        >
                          {item.value}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} lg={7}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      Invoices
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#F5F7FB" }}>
                            <TableCell sx={{ fontWeight: 700 }}>
                              Invoice
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Due</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              Status
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              Amount
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              Paid
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {debtor.invoices.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6} align="center">
                                No invoices found.
                              </TableCell>
                            </TableRow>
                          ) : (
                            debtor.invoices.map((invoice, index) => (
                              <TableRow key={getInvoiceRowKey(invoice, index)}>
                                <TableCell>
                                  <Typography fontWeight={600}>
                                    {invoice.invoiceNo}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {invoice.description || "No description"}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {formatDate(invoice.invoiceDate)}
                                </TableCell>
                                <TableCell>
                                  {formatDate(invoice.dueDate)}
                                </TableCell>
                                <TableCell>
                                  <Chip label={invoice.status} size="small" />
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(invoice.amountIncl)}
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(invoice.amountPaid || 0)}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} lg={5}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      Receipts & Payments
                    </Typography>

                    <Box
                      sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
                    >
                      <Tabs
                        value={receiptTabValue}
                        onChange={(e, newValue) => setReceiptTabValue(newValue)}
                        aria-label="receipt types"
                      >
                        <Tab label={`All (${debtor.receipts.length})`} />
                        <Tab
                          label={`Installments (${installmentReceipts.length})`}
                        />
                        <Tab label={`VAT (${vatReceipts.length})`} />
                      </Tabs>
                    </Box>

                    <TabPanel value={receiptTabValue} index={0}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: "#F5F7FB" }}>
                              <TableCell sx={{ fontWeight: 700 }}>
                                Receipt
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>
                                Type
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>
                                Date
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                Amount
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {debtor.receipts.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={4} align="center">
                                  No receipts found.
                                </TableCell>
                              </TableRow>
                            ) : (
                              debtor.receipts.map((receipt, index) => (
                                <TableRow
                                  key={getReceiptRowKey(receipt, index)}
                                >
                                  <TableCell>
                                    <Typography fontWeight={600}>
                                      {receipt.receiptNo}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {receipt.reference ||
                                        receipt.notes ||
                                        "-"}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={
                                        receipt.paymentType ||
                                        receipt.receiptType ||
                                        "other"
                                      }
                                      size="small"
                                      variant="outlined"
                                      color={
                                        receipt.paymentType === "vat" ||
                                        receipt.receiptType === "vat"
                                          ? "warning"
                                          : "default"
                                      }
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {formatDate(receipt.receiptDate)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(receipt.amount)}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </TabPanel>

                    <TabPanel value={receiptTabValue} index={1}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: "#F5F7FB" }}>
                              <TableCell sx={{ fontWeight: 700 }}>
                                Receipt
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>
                                Date
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>
                                Method
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                Amount
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {installmentReceipts.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={4} align="center">
                                  No installment payments found.
                                </TableCell>
                              </TableRow>
                            ) : (
                              installmentReceipts.map((receipt, index) => (
                                <TableRow
                                  key={getReceiptRowKey(receipt, index)}
                                >
                                  <TableCell>
                                    <Typography fontWeight={600}>
                                      {receipt.receiptNo}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {receipt.reference ||
                                        receipt.notes ||
                                        "-"}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    {formatDate(receipt.receiptDate)}
                                  </TableCell>
                                  <TableCell>
                                    {receipt.cashbook || "-"}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(receipt.amount)}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </TabPanel>

                    <TabPanel value={receiptTabValue} index={2}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: "#F5F7FB" }}>
                              <TableCell sx={{ fontWeight: 700 }}>
                                Receipt
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>
                                Date
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>
                                Method
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                Amount
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {vatReceipts.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={4} align="center">
                                  No VAT payments found.
                                </TableCell>
                              </TableRow>
                            ) : (
                              vatReceipts.map((receipt, index) => (
                                <TableRow
                                  key={getReceiptRowKey(receipt, index)}
                                >
                                  <TableCell>
                                    <Typography fontWeight={600}>
                                      {receipt.receiptNo}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {receipt.reference ||
                                        receipt.notes ||
                                        "-"}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    {formatDate(receipt.receiptDate)}
                                  </TableCell>
                                  <TableCell>
                                    {receipt.cashbook || "-"}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(receipt.amount)}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </TabPanel>

                    <Box
                      sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}
                    >
                      {debtor.email ? (
                        <Chip
                          icon={<EmailIcon />}
                          label={debtor.email}
                          variant="outlined"
                        />
                      ) : null}
                      {debtor.secondaryEmail ? (
                        <Chip
                          icon={<EmailIcon />}
                          label={debtor.secondaryEmail}
                          variant="outlined"
                          color="primary"
                        />
                      ) : null}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        ) : null}
      </Box>
    </FinanceContainer>
  );
};

export default DebtorDetail;
